import { useState } from "react";
import { api } from "@/lib/api";
import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";
import { StatusBadge } from "@/components/tool-results";

export function BlacklistCheckerPage() {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const runCheck = async () => {
    if (!domain) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await api.tools.securityScan(domain);
      setResult(data);
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Free Domain Blacklist Checker",
    "description": "Check if your domain or IP is on any email blacklists. Instant blacklist lookup across multiple DNS blocklists. No signup required.",
    "url": "https://getsent.dev/tools/blacklist-checker",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is a domain blacklist?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A domain blacklist (also called a blocklist or DNSBL) is a list of domains or IP addresses that have been flagged for sending spam, malware, or other malicious content. Email providers check these lists and may reject or filter emails from blacklisted senders."
        }
      },
      {
        "@type": "Question",
        "name": "Why is my domain blacklisted?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Common reasons include: sending spam (even unintentionally), compromised email accounts, malware infections, poor email list hygiene, high bounce rates, or sharing an IP with spammers. Sometimes domains are listed due to false positives."
        }
      },
      {
        "@type": "Question",
        "name": "How do I get my domain removed from a blacklist?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "First, identify and fix the underlying issue (spam, malware, compromised accounts). Then visit each blacklist's website and submit a removal request. Most blacklists have a self-service delisting process. Some automatically delist after 1-2 weeks if no further issues occur."
        }
      },
      {
        "@type": "Question",
        "name": "How does being blacklisted affect email deliverability?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Being blacklisted can severely impact email deliverability. Your emails may be rejected outright, sent to spam folders, or delayed. Major email providers like Gmail, Outlook, and Yahoo check blacklists when deciding whether to accept incoming mail."
        }
      }
    ]
  };

  return (
    <div>
      <SEO
        title="Free Domain Blacklist Checker - Is My Domain Blacklisted?"
        description="Check if your domain or IP is blacklisted. Instant lookup across multiple DNS blocklists (DNSBLs). Find out why your emails aren't being delivered. Free - no signup."
        canonical="/tools/blacklist-checker"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-red-50/50 to-transparent py-16 dark:from-red-950/20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-red-500/10 px-4 py-1.5 text-sm font-medium text-red-600">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              Free Tool
            </div>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              Domain Blacklist{" "}
              <span className="bg-gradient-to-r from-red-500 to-orange-600 bg-clip-text text-transparent">
                Checker
              </span>
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Is your domain blacklisted? Check instantly across multiple DNS blocklists
              to find out why your emails aren't being delivered.
            </p>
          </div>
        </div>
      </section>

      {/* Tool */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="rounded-2xl border bg-card p-6 shadow-lg">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <label className="mb-2 block text-sm font-medium">Enter your domain</label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="example.com"
                  className="w-full rounded-lg border bg-background px-4 py-3 text-base focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20"
                  onKeyDown={(e) => e.key === "Enter" && runCheck()}
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={runCheck}
                  disabled={loading || !domain}
                  className="w-full rounded-lg bg-gradient-to-r from-red-500 to-orange-600 px-8 py-3 font-semibold text-white shadow-lg shadow-red-500/25 transition-all hover:shadow-xl hover:shadow-red-500/30 disabled:opacity-50 sm:w-auto"
                >
                  {loading ? "Checking..." : "Check Blacklists"}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-red-600">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-6">
              <BlacklistResult data={result} />
            </div>
          )}
        </div>
      </section>

      {/* What is a Blacklist */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">What is a Domain Blacklist?</h2>
          <div className="mt-4 space-y-4 text-muted-foreground">
            <p>
              A <strong className="text-foreground">domain blacklist</strong> (also called a blocklist or DNSBL)
              is a database of domains and IP addresses that have been flagged for sending spam, malware,
              or other unwanted content.
            </p>
            <p>
              Email providers like Gmail, Outlook, and Yahoo check these blacklists when receiving email.
              If your domain or sending IP is listed, your emails may be:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>Rejected outright (bounced)</li>
              <li>Sent directly to spam/junk folders</li>
              <li>Delayed or throttled</li>
              <li>Flagged with warning messages</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Common Reasons */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Why Domains Get Blacklisted</h2>
          <div className="mt-6 grid gap-4">
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold text-red-600">Sending Spam</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Sending unsolicited bulk email, even unintentionally. This includes purchased email lists
                or sending to people who didn't explicitly opt in.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold text-red-600">Compromised Accounts</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                If an email account on your domain is hacked, attackers often use it to send spam
                or phishing emails, getting your domain blacklisted.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold text-red-600">Malware Infection</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Malware on your servers or website can send spam without your knowledge,
                or your domain may be flagged for hosting malicious content.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold text-red-600">Poor List Hygiene</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                High bounce rates from outdated email lists or sending to spam traps
                signals to blacklists that you're not following best practices.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold text-amber-600">Shared IP Reputation</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                If you share an IP address with spammers (common with shared hosting),
                you may be blacklisted through no fault of your own.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How to Get Delisted */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">How to Get Removed from a Blacklist</h2>
          <div className="mt-6 space-y-6">
            <div className="flex gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-500/10 text-sm font-bold text-red-600">1</div>
              <div>
                <h3 className="font-semibold">Identify the Problem</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Review your email sending practices, check for compromised accounts,
                  scan for malware, and audit your email lists for invalid addresses.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-500/10 text-sm font-bold text-red-600">2</div>
              <div>
                <h3 className="font-semibold">Fix the Root Cause</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Remove malware, secure compromised accounts, clean your email lists,
                  and implement proper opt-in procedures. Don't request delisting until the issue is resolved.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-500/10 text-sm font-bold text-red-600">3</div>
              <div>
                <h3 className="font-semibold">Request Removal</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Visit each blacklist's website and follow their delisting process.
                  Most have a self-service form. Be honest about what happened and what you've fixed.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-500/10 text-sm font-bold text-red-600">4</div>
              <div>
                <h3 className="font-semibold">Monitor & Prevent</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Set up monitoring to catch future issues early. Implement email authentication
                  (SPF, DKIM, DMARC) and maintain good sending practices.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
          <div className="mt-6 space-y-6">
            <div>
              <h3 className="font-semibold">What is a domain blacklist?</h3>
              <p className="mt-2 text-muted-foreground">
                A domain blacklist (also called a blocklist or DNSBL) is a list of domains or IP addresses
                that have been flagged for sending spam, malware, or other malicious content. Email providers
                check these lists and may reject or filter emails from blacklisted senders.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">Why is my domain blacklisted?</h3>
              <p className="mt-2 text-muted-foreground">
                Common reasons include: sending spam (even unintentionally), compromised email accounts,
                malware infections, poor email list hygiene, high bounce rates, or sharing an IP with spammers.
                Sometimes domains are listed due to false positives.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">How do I get my domain removed from a blacklist?</h3>
              <p className="mt-2 text-muted-foreground">
                First, identify and fix the underlying issue (spam, malware, compromised accounts).
                Then visit each blacklist's website and submit a removal request. Most blacklists have
                a self-service delisting process. Some automatically delist after 1-2 weeks if no further issues occur.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">How does being blacklisted affect email deliverability?</h3>
              <p className="mt-2 text-muted-foreground">
                Being blacklisted can severely impact email deliverability. Your emails may be rejected outright,
                sent to spam folders, or delayed. Major email providers like Gmail, Outlook, and Yahoo check
                blacklists when deciding whether to accept incoming mail.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">How often should I check if my domain is blacklisted?</h3>
              <p className="mt-2 text-muted-foreground">
                We recommend checking at least monthly, or immediately if you notice deliverability issues
                like increased bounces or emails going to spam. Continuous monitoring with alerts is ideal
                for catching problems early.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Tools */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center">Related Tools</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Link
              to="/tools/email-authentication-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-red-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-red-600">Email Authentication Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Check SPF, DKIM, and DMARC to prevent spoofing.
              </p>
            </Link>
            <Link
              to="/tools/website-security-scanner"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-red-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-red-600">Website Security Scanner</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Full security scan including SSL and headers.
              </p>
            </Link>
            <Link
              to="/tools/spf-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-red-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-red-600">SPF Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Validate your SPF record configuration.
              </p>
            </Link>
            <Link
              to="/tools/dmarc-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-red-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-red-600">DMARC Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Check your DMARC policy for email protection.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t py-16">
        <div className="mx-auto max-w-7xl px-6 text-center lg:px-8">
          <h2 className="text-2xl font-bold sm:text-3xl">
            Want continuous blacklist monitoring?
          </h2>
          <p className="mt-2 text-muted-foreground">
            Get alerts the moment your domain appears on a blacklist. Free for one domain.
          </p>
          <Link
            to="/register"
            className="mt-6 inline-flex rounded-full bg-gradient-to-r from-red-500 to-orange-600 px-8 py-3 font-semibold text-white shadow-lg shadow-red-500/25 transition-all hover:shadow-xl hover:shadow-red-500/30"
          >
            Start free monitoring
          </Link>
        </div>
      </section>
    </div>
  );
}

function BlacklistResult({ data }: { data: any }) {
  const { blacklist } = data;

  return (
    <div className="space-y-4">
      {/* Main Result */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Blacklist Status</h3>
            <p className="text-sm text-muted-foreground">{data.domain}</p>
          </div>
          <StatusBadge
            status={blacklist.clean ? "valid" : "invalid"}
            label={blacklist.clean ? "Clean" : "Blacklisted"}
          />
        </div>

        {blacklist.clean ? (
          <div className="mt-4 rounded-lg bg-green-500/10 p-4">
            <div className="flex items-center gap-2 text-green-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium">Your domain is not on any blacklists we checked!</span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              We checked {blacklist.checked?.length || 0} major blacklists and your domain is clean.
            </p>
          </div>
        ) : (
          <div className="mt-4 rounded-lg bg-red-500/10 p-4">
            <div className="flex items-center gap-2 text-red-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <span className="font-medium">Your domain is listed on {blacklist.detectedOn?.length || 0} blacklist(s)</span>
            </div>
            <div className="mt-3">
              <p className="text-sm font-medium text-red-600 mb-2">Listed on:</p>
              <ul className="space-y-1">
                {blacklist.detectedOn?.map((list: string, i: number) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <svg className="h-4 w-4 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {list}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Checked Lists */}
      <div className="rounded-xl border bg-card p-6">
        <h3 className="text-lg font-semibold">Blacklists Checked</h3>
        <p className="text-sm text-muted-foreground mb-4">
          We checked {blacklist.checked?.length || 0} major DNS blocklists
        </p>
        <div className="flex flex-wrap gap-2">
          {blacklist.checked?.map((list: string, i: number) => (
            <span
              key={i}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${
                blacklist.detectedOn?.includes(list)
                  ? "bg-red-500/10 text-red-600"
                  : "bg-green-500/10 text-green-600"
              }`}
            >
              {blacklist.detectedOn?.includes(list) ? (
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              )}
              {list}
            </span>
          ))}
        </div>
        {blacklist.errors?.length > 0 && (
          <div className="mt-4 text-sm text-amber-600">
            <p className="font-medium">Some checks failed:</p>
            {blacklist.errors.map((e: string, i: number) => <p key={i}>{e}</p>)}
          </div>
        )}
      </div>

      {/* Next Steps */}
      {!blacklist.clean && (
        <div className="rounded-xl border bg-card p-6">
          <h3 className="text-lg font-semibold">Recommended Next Steps</h3>
          <ol className="mt-4 space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500/10 text-xs font-bold text-red-600">1</span>
              <span>Check for compromised accounts or malware on your systems</span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500/10 text-xs font-bold text-red-600">2</span>
              <span>Review your email sending practices and clean your mailing lists</span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500/10 text-xs font-bold text-red-600">3</span>
              <span>Visit each blacklist's website to request removal</span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500/10 text-xs font-bold text-red-600">4</span>
              <span>Set up email authentication (SPF, DKIM, DMARC) to prevent future issues</span>
            </li>
          </ol>
        </div>
      )}
    </div>
  );
}
